import { postcodeValidator, postcodeValidatorExistsForCountry } from "postcode-validator";
import luhn from "fast-luhn";
import {
  ErrorId,
  FieldId,
  FieldContent,
  FieldValidationErrors,
  ViewModel,
  MilitaryStatus,
  Organization,
  Metadata,
  FirstResponderStatus,
  MedicalProfessionalStatus,
  ExtendedFieldValidationErrors,
  ExtendedFieldId,
  Validator,
  ExtendedErrorId,
  DocUploadViewModel,
  FormValidationOptions,
  WithCoreFields,
  InactiveMilitaryPersonalInfoViewModel,
  Country,
  FIRST_RESPONDER_STATUS,
  MEDICAL_PROFESSIONAL_STATUS,
  MILITARY_STATUS,
  LowIncomePersonalInfoViewModel,
  RECENT_MOVER_STATUS,
  RecentMoverStatus,
} from "../types/types";
import { assertValidFieldId } from "../types/assertions";
import {
  getFieldValidationErrorsEmpty,
  getExtendedFieldValidationErrorsEmpty,
} from "../types/empties";
import { ACCEPTED_DOC_MIME_TYPES, AUTH_TOKEN_LENGTH, SSN_STRING_LENGTH } from "../../constants";
import { logger } from "../utils/logger/logger";
import { FieldIdEnum, StateEnum } from "../types/runtimeTypes";
import { getOverridenValidator } from "./validatorOverride";
import { getCustomValidatorFields, getCustomValidator } from "./customValidators";
import { getSafe } from "../utils/getSafe";
import { getOptions } from "../../options/options";

const DECIMAL_RADIX = 10;
const ADOLESCENT_AGE = 16;
const MIN_BIRTHYEAR = 1900;
const MAX_BIRTHYEAR = 3000;

export const MAX_METADATA_KEYS = 50;
export const MAX_METADATA_KEY_LENGTH = 256;
export const MAX_METADATA_VALUE_LENGTH = 4096;

export const INTL_PHONE_NUMBER_REGEX = /^\+(?:[0-9] ?){6,14}[0-9]$/;

export const VALID_PHONE_NUMBER_REGEXPS = [
  /^\+?0*\d{10,}(x[0-9]{0,5})?$/,
  /(^\D?(\d{3})\D?\D?(\d{3})\D?(\d{4})$)/,
];

type GetValidationError = (
  value: string,
  formValidationOptions?: FormValidationOptions,
) => ErrorId | undefined;

export const validateMetadata = (metadata: Metadata): boolean => {
  const metadataKeyCount = Object.keys(metadata).length;

  // TOOD: Other metadata restrictions?
  if (metadataKeyCount > MAX_METADATA_KEYS) {
    logger.error(`Invalid number of metadata keys (max ${MAX_METADATA_KEYS})`, "metadata keys");
    return false;
  }
  return true;
};

export const ensureMaxMetadataKeyValueLengths = (metadata: Metadata): Metadata => {
  const modifiedMetadata: Metadata = {};
  // TODO fix next line
  // eslint-disable-next-line
  for (const key in metadata) {
    let newKey = key;
    let newValue = metadata[key] as string;

    if (newValue.length > MAX_METADATA_VALUE_LENGTH) {
      logger.warn(
        `Truncating metadata value '${metadata[key]}' for key '${newKey}' (max length ${MAX_METADATA_VALUE_LENGTH} characters).`,
      );
      newValue = newValue.substring(0, MAX_METADATA_VALUE_LENGTH);
    }
    if (newKey.length > MAX_METADATA_KEY_LENGTH) {
      logger.warn(
        `Truncating metadata key '${key}' (max length ${MAX_METADATA_KEY_LENGTH} characters).`,
      );
      newKey = newKey.substring(0, MAX_METADATA_KEY_LENGTH);
    }

    modifiedMetadata[newKey] = newValue;
  }
  return modifiedMetadata;
};

export const getFieldValidationErrors = (
  viewModel: ViewModel,
  formValidationOptions?: FormValidationOptions,
): FieldValidationErrors => {
  const fieldValidationErrors = getEmptyFieldValidationErrors();
  const fieldsToSkip = getSafe(() => viewModel.fieldsToSkipValidation);
  const metadata = getSafe(() => (viewModel as WithCoreFields).metadata, null);

  // TODO fix next line
  // eslint-disable-next-line
  for (const fieldId in fieldValidationErrors) {
    if (viewModel.hasOwnProperty(fieldId)) {
      if (Array.isArray(fieldsToSkip) && fieldsToSkip.indexOf(fieldId as FieldId) > -1) {
        continue;
      }
      const fieldValue = viewModel[fieldId] ? viewModel[fieldId] : "";
      fieldValidationErrors[fieldId] = validateFieldById(
        fieldId as FieldId,
        fieldValue,
        formValidationOptions,
      );
    } else if (
      metadata !== null &&
      typeof metadata === "object" &&
      metadata.hasOwnProperty(fieldId)
    ) {
      const fieldValue = metadata[fieldId] ? metadata[fieldId] : "";
      fieldValidationErrors[fieldId] = validateFieldById(
        fieldId as FieldId,
        fieldValue,
        formValidationOptions,
      );
    }
  }

  const { customFormFields } = getOptions();
  if (customFormFields) {
    customFormFields.forEach((customField) => {
      let errorId;
      try {
        errorId = customField.validate(viewModel.metadata[customField.fieldId]);
      } catch (e) {
        logger.warn(`Error validating custom field ${customField.fieldId}`, e);
      }
      if (errorId) {
        fieldValidationErrors[customField.fieldId] = errorId;
      } else {
        delete fieldValidationErrors[customField.fieldId];
      }
    });
  }

  const { formFieldConfig } = getOptions();
  if (formFieldConfig) {
    formFieldConfig.forEach((formField) => {
      const fieldValue = viewModel[formField.fieldId] ? viewModel[formField.fieldId] : "";
      const fieldInViewModel = viewModel.hasOwnProperty(formField.fieldId);
      const shouldSkipValidation = fieldsToSkip?.includes(formField.fieldId as FieldId);

      if (fieldInViewModel && formField.requireIfOptional && !shouldSkipValidation) {
        fieldValidationErrors[formField.fieldId] = validateFieldById(
          formField.fieldId as FieldId,
          fieldValue,
          formValidationOptions,
        );
      }
    });
  }

  return fieldValidationErrors;
};

export const isFormFilled = (
  viewModel: ViewModel,
  formValidationOptions?: FormValidationOptions,
): boolean => {
  const customValidatorFields = getCustomValidatorFields();

  // TODO fix next line
  // eslint-disable-next-line
  for (const fieldId in viewModel) {
    const fieldValue = viewModel[fieldId] ? viewModel[fieldId] : "";
    const isFieldSkipped =
      viewModel.fieldsToSkipValidation &&
      viewModel.fieldsToSkipValidation.includes(fieldId as FieldId);

    if (
      FieldIdEnum[fieldId] &&
      typeof validateFieldById(fieldId, fieldValue, formValidationOptions) === "string" &&
      !isFieldSkipped
    ) {
      return false;
    }
  }

  const metadata = getSafe(() => (viewModel as WithCoreFields).metadata, null);
  if (metadata !== null) {
    // TODO fix next line
    // eslint-disable-next-line
    for (const fieldId in metadata) {
      const fieldValue = metadata[fieldId] !== undefined ? metadata[fieldId] : "";

      if (
        customValidatorFields.includes(fieldId) &&
        typeof getCustomValidator(fieldId)(fieldValue, formValidationOptions) === "string"
      ) {
        return false;
      }
    }
  }

  return true;
};

export const isFormErrored = (currentFieldValidationErrors: FieldValidationErrors): boolean => {
  // TODO fix next line
  // eslint-disable-next-line
  for (const field in currentFieldValidationErrors) {
    if (currentFieldValidationErrors[field]) {
      logger.info(`Field '${field}' has errorId '${currentFieldValidationErrors[field]}'`);
      return true;
    }
  }
  return false;
};

const getValidationError = (
  fieldId: FieldId,
  value: FieldContent,
  defaultValidator: Validator,
  overriddenValidator?: Validator,
  formValidationOptions?: FormValidationOptions,
): ErrorId => {
  if (overriddenValidator) {
    logger.info(`Using overridden validator for field '${fieldId}'`);
    return overriddenValidator(value, formValidationOptions) as ErrorId;
  }
  return defaultValidator(value, formValidationOptions) as ErrorId;
};

const getEmptyFieldValidationErrors = (): FieldValidationErrors => {
  const emptyFieldValidationErrors: FieldValidationErrors = getFieldValidationErrorsEmpty();
  const emptyExtendedFieldValidationErrors: ExtendedFieldValidationErrors =
    getExtendedFieldValidationErrorsEmpty();
  const fieldValidationErrors: FieldValidationErrors = {
    ...emptyFieldValidationErrors,
    ...emptyExtendedFieldValidationErrors,
  };
  return fieldValidationErrors;
};

const getCustomValidationError = (
  fieldId: ExtendedFieldId,
  value: FieldContent,
  formValidationOptions?: FormValidationOptions,
): ExtendedErrorId => {
  const customValidator: Validator = getCustomValidator(fieldId);
  logger.info(`Using custom validator for custom field '${fieldId}'`);
  return customValidator ? customValidator(value, formValidationOptions) : undefined;
};

const customValidatorExists = (fieldId: ExtendedFieldId): boolean => {
  const customValidatorFields = getCustomValidatorFields();

  if (customValidatorFields.length && customValidatorFields.indexOf(fieldId) >= 0) {
    return true;
  }
  return false;
};

const getFirstNameValidationError = (value: FieldContent): ErrorId | undefined => {
  const invalidError: ErrorId = "invalidFirstName";

  if (value === undefined || !isValidName(value as string)) {
    return invalidError;
  }
};

const getLastNameValidationError = (value: string): ErrorId | undefined => {
  const invalidError: ErrorId = "invalidLastName";

  if (value === undefined || !isValidName(value)) {
    return invalidError;
  }
};

const getMemberIdError = (value: string): ErrorId | undefined => {
  const invalidError: ErrorId = "invalidMemberId";

  if (value === undefined || value === "") {
    return invalidError;
  }
};

const getCountryValidationError = (value: string): ErrorId | undefined => {
  const invalidError: ErrorId = "invalidCountry";
  if (!value) {
    return invalidError;
  }
};

export const getEmailValidationError = (value: string): ErrorId | undefined => {
  const invalidError: ErrorId = "invalidEmail";
  const VALID_EMAIL_REGEXP =
    /(^\s*?[A-Za-z0-9!#$%&''*+\/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&''*+\/=?^_`{|}~-]+)*@([A-Za-z0-9!#$%&''*+\/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&''*+\/=?^_`{|}~-]+)+|\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])\s*?$)/;

  if (value === undefined || !VALID_EMAIL_REGEXP.test(value)) {
    return invalidError;
  }
};

export const getPhoneNumberValidationError =
  (failIfValueFalsy: boolean, options?: { country: Country }) =>
  (value: string): ErrorId => {
    const invalidPhoneNumber: ErrorId = "invalidPhoneNumber";
    if (!value) {
      return failIfValueFalsy ? invalidPhoneNumber : undefined;
    }

    if (options?.country && options.country !== "US" && !INTL_PHONE_NUMBER_REGEX.test(value)) {
      return invalidPhoneNumber;
    }

    const matchedPatterns = VALID_PHONE_NUMBER_REGEXPS.filter((regexp: RegExp) =>
      regexp.test(value),
    );

    if (matchedPatterns.length === 0) {
      return invalidPhoneNumber;
    }
  };

const getPhoneNumberValidator = (formValidationOptions?: FormValidationOptions): Validator => {
  const country = formValidationOptions?.viewModel?.countryChoice?.value;
  const shouldFailIfEmpty =
    (formValidationOptions && formValidationOptions.smsLoopEnabled) ||
    // restricting the non-US empty check to age programs to prevent any possible
    // leakage in case optional phone numbers are allowed on other program types.
    (formValidationOptions?.currentStep === "collectAgePersonalInfo" &&
      country &&
      country !== "US");
  return getPhoneNumberValidationError(shouldFailIfEmpty, { country });
};

const getOrganizationValidationError = (value: Organization): ErrorId | undefined => {
  const invalidError: ErrorId = "invalidOrganization";
  if (
    value.id === undefined ||
    Number.isNaN(value.id) ||
    (value.source !== "EMPLOYER" && value.id < 0) ||
    (value.source === "EMPLOYER" && (value.id > -1 || !value.idExtended)) ||
    (value.id === 0 && value.name.length <= 0)
  ) {
    return invalidError;
  }
};

const isBirthDateEmpty = (birthDate: any) => {
  const noUndefined = birthDate.split("undefined").join("");
  return noUndefined.length <= 2;
};

const getBirthDateValidationError = (
  value: string,
  formValidationOptions?: FormValidationOptions,
): ErrorId | undefined => {
  const invalidError: ErrorId = "invalidBirthDate";
  const underagePersonError: ErrorId = "underagePerson";
  const futureBirthDateError: ErrorId = "futureBirthDate";
  const outsideAgeError: ErrorId = "outsideAgePerson";
  const minAge = formValidationOptions?.minAge ?? undefined;
  const maxAge = formValidationOptions?.maxAge ?? undefined;

  if (getSafe(() => formValidationOptions.currentStep === "collectMemberPersonalInfo")) {
    if (value === undefined || value === "" || isBirthDateEmpty(value)) return;
    if (!isValidDateFormat(value) || !isValidDate(value)) {
      return invalidError;
    }
  } else if (value === undefined || !isValidDateFormat(value) || !isValidDate(value)) {
    return invalidError;
  }

  if (isAgeOutsideRange(value, minAge, maxAge)) {
    return outsideAgeError;
  }
  if (isFutureDate(value)) {
    return futureBirthDateError;
  }
  if (isUnderageDate(value, minAge)) {
    return underagePersonError;
  }
};

const getFileValidationError = (
  file: File,
  formValidationOptions?: FormValidationOptions,
): ErrorId | undefined => {
  const hasFileExtension = (fileName: string): boolean => fileName && fileName.includes(".");
  const noValidFilesError: ErrorId = "noValidFiles";
  const invalidFileSizeMaxError: ErrorId = "invalidFileSizeMax";
  const invalidFileSizeEmptyError: ErrorId = "invalidFileSizeEmpty";
  const unsupportedDocMimeTypeError: ErrorId = "unsupportedDocMimeType";
  const duplicateFileError = "duplicateFile";
  const FILE_SIZE_10MB = 10485760;
  const validFileTypes: string[] = ACCEPTED_DOC_MIME_TYPES;
  if (formValidationOptions) {
    const vm = formValidationOptions.viewModel as DocUploadViewModel;
    const selectedfiles = [vm.file1, vm.file2, vm.file3];

    const isDuplicate = selectedfiles.some(
      (f) => f !== undefined && f.name === file.name && f.size === file.size,
    );
    if (isDuplicate) {
      return duplicateFileError;
    }
  }

  if (file === undefined) {
    return noValidFilesError;
  }
  // If no extension, allow upload so API can determine if valid mime type
  if (hasFileExtension(file.name) && !validFileTypes.includes(file.type)) {
    return unsupportedDocMimeTypeError;
  }
  if (file.size > FILE_SIZE_10MB) {
    return invalidFileSizeMaxError;
  }
  if (file.size <= 0) {
    return invalidFileSizeEmptyError;
  }
};

export const getEbtCardValidationError: GetValidationError = (value, formValidationOptions) => {
  const vm = formValidationOptions.viewModel as LowIncomePersonalInfoViewModel;
  if (vm.statuses && vm.statuses.includes("SNAP_BENEFITS")) {
    if (!value.match(/^\d{16,19}$/)) {
      // Must be 16-19 digits
      return "invalidEbtCardNumber";
    }

    if (value.startsWith("610098") && value.length !== 19) {
      // this prefix must be 19 digits
      return "invalidEbtCardNumber";
    }

    const checkSumExemptPrefixes = ["600486", "507712", "507714"] as const;
    const isExempt = checkSumExemptPrefixes.some((pref) => value.startsWith(pref));
    if (!isExempt && !luhn(value)) {
      // Failed checksum
      return "invalidEbtCardNumber";
    }
  }
};

export const getPostalCodeValidationError = (
  value: string,
  formValidationOptions?: FormValidationOptions,
): ErrorId | undefined => {
  const invalidPostalCode: ErrorId = "invalidPostalCode";
  const countryCode = getSafe(() => formValidationOptions.viewModel.countryChoice.value, "US");

  if (isValidPostalCode(countryCode, value)) {
    return;
  }
  return invalidPostalCode;
};

const getAddress1ValidationError = (value: string): ErrorId | undefined => {
  const invalidError: ErrorId = "invalidAddress";
  if (!value) {
    return invalidError;
  }
};

const getCityValidationError = (value: string): ErrorId | undefined => {
  const invalidError: ErrorId = "invalidCity";
  if (!value) {
    return invalidError;
  }
};

const getStateValidationError = (value: string): ErrorId | undefined => {
  const invalidError: ErrorId = "invalidState";
  if (!isValidState(value)) {
    return invalidError;
  }
};

const existsAndIsValidDate = (value?: string): boolean =>
  value !== undefined && isValidDateFormat(value) && isValidDate(value);

const isBeforeBirthdate = (value: string, vm: ViewModel): boolean => {
  const date = new Date(value);
  const birthDate = new Date((vm as InactiveMilitaryPersonalInfoViewModel).birthDate);

  return date < birthDate;
};

const getDischargeDateValidationError = (
  value: string,
  formValidationOptions?: FormValidationOptions,
): ErrorId | undefined => {
  const transitionalMilitaryValidityDays = formValidationOptions?.transitionalMilitaryValidityDays;
  const shouldValidateTransitionalMilitaryValidityDays = !!transitionalMilitaryValidityDays;
  if (!existsAndIsValidDate(value)) {
    return "invalidDischargeDate";
  }
  if (!shouldValidateTransitionalMilitaryValidityDays && isFutureDate(value)) {
    return "futureDischargeDate";
  }
  if (isBeforeBirthdate(value, formValidationOptions.viewModel)) {
    return "dischargeDateBeforeBirthDate";
  }
  if (
    shouldValidateTransitionalMilitaryValidityDays &&
    !isWithinTransitionalMilitaryValidityDays(value, transitionalMilitaryValidityDays)
  ) {
    return "invalidDischargeDate";
  }
};

const getActiveDutyStartDateValidationError = (
  value: string,
  formValidationOptions?: FormValidationOptions,
): ErrorId | undefined => {
  if (!value && !formValidationOptions.strictMilitaryValidationEnabled) {
    // a falsey value is valid unless strictMilitaryValidation is enabled.
    return;
  }

  if (!existsAndIsValidDate(value)) {
    return "invalidActiveDutyStartDate";
  }
  if (isFutureDate(value)) {
    return "futureActiveDutyStartDate";
  }
  if (isBeforeBirthdate(value, formValidationOptions.viewModel)) {
    return "activeDutyStartDateBeforeBirthDate";
  }
};

const getSsnValidationError = (value: number): ErrorId | undefined => {
  const invalidSocialSecurityNumber: ErrorId = "invalidSocialSecurityNumber";
  if (value.toString().length !== SSN_STRING_LENGTH) {
    return invalidSocialSecurityNumber;
  }
};

const isValidLicensedProfessionalStatus = (value: string) => value.length > 0;

const isValidLowIncomeStatus = (value: string) => value.length > 0;

const getDriverLicenseNumberValidationError = (value: string): ErrorId | undefined => {
  const invalidDriverLicenseNumber: ErrorId = "invalidDriverLicenseNumber";
  const VALID_DRIVER_LICENSE_REGEXP = /^[A-Za-z0-9-\*]{1,100}$/; // '*' is sometimes used in WA state

  if (value === undefined || !VALID_DRIVER_LICENSE_REGEXP.test(value)) {
    return invalidDriverLicenseNumber;
  }
};

const getStatusesValidationError = (value: string): ErrorId | undefined => {
  const invalidStatuses: ErrorId = "invalidStatuses";
  if (!value || !isValidLicensedProfessionalStatus(value) || !isValidLowIncomeStatus(value)) {
    return invalidStatuses;
  }
};

const isValidMilitaryStatus = (value: string) => MILITARY_STATUS.includes(value as MilitaryStatus);

const isValidFirstResponderStatus = (value: string): boolean =>
  FIRST_RESPONDER_STATUS.includes(value as FirstResponderStatus);

const isValidMedicalProfessionalStatus = (value: string) =>
  MEDICAL_PROFESSIONAL_STATUS.includes(value as MedicalProfessionalStatus);

const isRecentMoverStatus = (value: string) =>
  RECENT_MOVER_STATUS.includes(value as RecentMoverStatus);

const getStatusValidationError = (value: string): ErrorId | undefined => {
  const invalidStatus: ErrorId = "invalidStatus";

  if (
    !value ||
    (!isValidMilitaryStatus(value) &&
      !isValidFirstResponderStatus(value) &&
      !isValidMedicalProfessionalStatus(value) &&
      !isRecentMoverStatus(value) &&
      !isValidLowIncomeStatus(value) &&
      !isValidLicensedProfessionalStatus(value))
  ) {
    return invalidStatus;
  }
};

const getAuthenticationCodeValidationError = (value: FieldContent): ErrorId | undefined => {
  const invalidError: ErrorId = "invalidAuthenticationLoopToken";

  if (value === undefined || value.toString().length !== AUTH_TOKEN_LENGTH) {
    return invalidError;
  }
};

export const getCvecNumberValidationError = (value: string): ErrorId | undefined => {
  const invalidError: ErrorId = "invalidCvecNumber";
  const VALID_CVEC_NUMBER_REGEXP = /^(?:[a-zA-Z0-9]\s*){12}$|^$/;

  if (value === undefined || !VALID_CVEC_NUMBER_REGEXP.test(value)) {
    return invalidError;
  }
};

export const isValidName = (value: string, opts?: { _test_backup_regex: boolean }): boolean => {
  try {
    const VALID_NAME_REGEXP = /([\p{Letter}\p{Mark}\p{Connector_Punctuation}\p{Join_Control}]+)/v;
    if (opts?._test_backup_regex) {
      throw new Error("testing backup regex");
    }
    return VALID_NAME_REGEXP.test(value);
  } catch {
    // unicode sets `/v` is unsupported in older browsers, notably iOS 15/16 so if that fails instead use a
    // simpler regex that will compile. It's less accurate but we have the safety of server-side validation.
    //
    // This regex matches most characters, but excludes some control characters, space, and ASCII digits (0-9).
    // eslint-disable-next-line no-control-regex
    const VALID_NAME_REGEXP = /([\u0000-\u0019\u0021-\u002F\u003A-\uFFFF]+)/;
    return VALID_NAME_REGEXP.test(value);
  }
};

const isValidDate = (value: string): boolean => {
  const dateParts = value.split("-");
  const dateYear = parseInt(dateParts[0], DECIMAL_RADIX);
  const dateMonth = parseInt(dateParts[1], DECIMAL_RADIX);
  const dateDay = parseInt(dateParts[2], DECIMAL_RADIX);
  const monthDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  if (dateYear < MIN_BIRTHYEAR || dateYear > MAX_BIRTHYEAR || dateMonth <= 0 || dateMonth > 12) {
    return false;
  }

  // Check for leap years
  if (dateYear % 400 === 0 || (dateYear % 100 !== 0 && dateYear % 4 === 0)) {
    monthDays[1] = 29;
  }

  if (dateDay <= 0 || dateDay > monthDays[dateMonth - 1]) {
    return false;
  }
  return true;
};

const isValidDateFormat = (value: string): boolean => {
  const VALID_DATE_FORMAT_REGEXP = /^\d{4}[\-\s\/]\d{1,2}[\-\s\/]\d{1,2}$/;

  if (VALID_DATE_FORMAT_REGEXP.test(value)) {
    return true;
  }
  return false;
};

const isFutureDate = (value: string): boolean => {
  const currentDate = new Date();
  const candidateDate = new Date(value);
  return candidateDate > currentDate;
};

const isWithinTransitionalMilitaryValidityDays = (
  value: string,
  transitionalMilitaryValidityDays: number,
): boolean => {
  const candidateDate = new Date(`${value}T00:00:00Z`);

  const now = new Date();
  const todayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

  const pastDischargeDateLimit = new Date(todayUTC.valueOf());
  pastDischargeDateLimit.setUTCDate(todayUTC.getUTCDate() - transitionalMilitaryValidityDays);

  const futureDischargeDateLimit = new Date(todayUTC.valueOf());
  futureDischargeDateLimit.setUTCDate(todayUTC.getUTCDate() + transitionalMilitaryValidityDays);

  return candidateDate >= pastDischargeDateLimit && candidateDate <= futureDischargeDateLimit;
};

const isUnderageDate = (value: string, minAge = ADOLESCENT_AGE): boolean => {
  const birthDateParts = value.split("-");
  const birthDateYear = parseInt(birthDateParts[0], DECIMAL_RADIX);
  const birthDateMonth = parseInt(birthDateParts[1], DECIMAL_RADIX);
  const birthDateDay = parseInt(birthDateParts[2], DECIMAL_RADIX);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentDay = new Date().getDate();

  if (currentYear - birthDateYear === minAge) {
    if (currentMonth - birthDateMonth < 0) {
      return true;
    }

    if (currentMonth - birthDateMonth === 0) {
      if (currentDay - birthDateDay < 0) {
        return true;
      }
    }
  } else if (currentYear - birthDateYear < minAge) {
    return true;
  }
  return false;
};

const isOverageDate = (value: string, maxAge = 120) => {
  const birthDateParts = value.split("-");
  const birthDateYear = parseInt(birthDateParts[0], DECIMAL_RADIX);
  const birthDateMonth = parseInt(birthDateParts[1], DECIMAL_RADIX);
  const birthDateDay = parseInt(birthDateParts[2], DECIMAL_RADIX);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentDay = new Date().getDate();

  if (currentYear - birthDateYear === maxAge) {
    if (currentMonth - birthDateMonth > 0) {
      return true;
    }

    if (currentMonth - birthDateMonth === 0) {
      if (currentDay - birthDateDay > 0) {
        return true;
      }
    }
  } else if (currentYear - birthDateYear > maxAge) {
    return true;
  }
  return false;
};

const isAgeOutsideRange = (value: string, minAge?: number, maxAge?: number): boolean => {
  let isUnderage = false;
  let isOverage = false;

  if (minAge) {
    isUnderage = isUnderageDate(value, minAge);
  }
  if (maxAge) {
    isOverage = isOverageDate(value, maxAge);
  }
  return isUnderage || isOverage;
};

export const isValidPostalCode = (countryCode: Country = "US", value: string) => {
  const countryCodeString = countryCode as any;
  if (postcodeValidatorExistsForCountry(countryCodeString)) {
    return postcodeValidator(value, countryCodeString);
  }
  return false;
};

/**
 * @deprecated Use `isValidPostalCode('US', value)` instead
 *  */
export const isValidUsPostalCode = (value: string): boolean => isValidPostalCode("US", value);

/**
 * @deprecated Use `isValidPostalCode('CA', value)` instead
 *  */
export const isValidCanadianPostalCode = (value: string): boolean => isValidPostalCode("CA", value);

const isValidState = (value: string) => {
  if (!value) {
    return false;
  }
  const validStateCodes: StateEnum[] = Object.values(StateEnum);
  if (validStateCodes.indexOf(value.toUpperCase() as StateEnum) >= 0) {
    return true;
  }
  return false;
};

export const validateFieldById = (
  fieldId: FieldId | ExtendedFieldId,
  value: FieldContent,
  formValidationOptions?: FormValidationOptions,
): ErrorId | ExtendedErrorId | undefined => {
  assertValidFieldId(fieldId);

  const defaultCoreFieldValidatorMap: Partial<Record<string, Validator>> = {
    [FieldIdEnum.firstName]: getFirstNameValidationError,
    [FieldIdEnum.lastName]: getLastNameValidationError,
    [FieldIdEnum.memberId]: getMemberIdError,
    [FieldIdEnum.email]: getEmailValidationError,
    [FieldIdEnum.organizationEmail]: getEmailValidationError,
    [FieldIdEnum.phoneNumber]: getPhoneNumberValidator(formValidationOptions),
    [FieldIdEnum.organization]: getOrganizationValidationError,
    [FieldIdEnum.birthDate]: getBirthDateValidationError,
    [FieldIdEnum.postalCode]: getPostalCodeValidationError,
    [FieldIdEnum.address1]: getAddress1ValidationError,
    [FieldIdEnum.city]: getCityValidationError,
    [FieldIdEnum.state]: getStateValidationError,
    [FieldIdEnum.country]: getCountryValidationError,
    [FieldIdEnum.dischargeDate]: getDischargeDateValidationError,
    [FieldIdEnum.activeDutyStartDate]: getActiveDutyStartDateValidationError,
    [FieldIdEnum.socialSecurityNumber]: getSsnValidationError,
    [FieldIdEnum.status]: getStatusValidationError,
    [FieldIdEnum.statuses]: getStatusesValidationError,
    [FieldIdEnum.driverLicenseNumber]: getDriverLicenseNumberValidationError,
    [FieldIdEnum.docUpload]: getFileValidationError,
    [FieldIdEnum.ebtCardNumber]: getEbtCardValidationError,
    [FieldIdEnum.authenticationCode]: getAuthenticationCodeValidationError,
    [FieldIdEnum.cvecNumber]: getCvecNumberValidationError,
  };

  const defaultValidator = defaultCoreFieldValidatorMap[fieldId];
  if (defaultValidator) {
    const overriddenValidator: Validator = getOverridenValidator(fieldId as FieldId);
    return getValidationError(
      fieldId as FieldId,
      value,
      defaultValidator,
      overriddenValidator,
      formValidationOptions,
    );
  }

  if (customValidatorExists(fieldId)) {
    return getCustomValidationError(fieldId, value, formValidationOptions);
  }

  return undefined;
};
